import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import AppLayout from "@/components/layout/AppLayout";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { useIsAdmin } from "@/hooks/useIsAdmin";
import { AdminUsersTable } from "@/components/admin/AdminUsersTable";
import { AdminOrganizationHierarchy } from "@/components/admin/AdminOrganizationHierarchy";
import { AdminAssessmentsTable } from "@/components/admin/AdminAssessmentsTable";
import { AdminAuditLogTable } from "@/components/admin/AdminAuditLogTable";
import { AdminQuestionnairesTable } from "@/components/admin/AdminQuestionnairesTable";
import AdminMfaPanel from "@/components/admin/AdminMfaPanel";
import { AdminQaAuditTab } from "@/components/admin/AdminQaAuditTab";
import { RoleDebug } from "@/components/RoleDebug";
import { ADMIN_METHODOLOGY_COMPOSITION_UI_ENABLED } from "@/config/compositionUiFlags";


const AdminConsole = () => {
  const location = useLocation();
  const { loading, isAdmin } = useIsAdmin();
  
  // Determine if we're navigating to a specific organization view
  const navState = location.state as any;
  const hasOrgNavigation = navState?.navigateTo === "subOrgs" || navState?.navigateTo === "portfolios";
  
  // Get the default tab from navigation state, auto-switch to orgs if coming from breadcrumb
  const initialTab = hasOrgNavigation ? "organizations" : (navState?.defaultTab || "users");
  const [activeTab, setActiveTab] = useState(initialTab);
  
  // Update active tab when navigation state changes (e.g., breadcrumb clicks)
  useEffect(() => {
    if (hasOrgNavigation) {
      setActiveTab("organizations");
    } else if (navState?.defaultTab) {
      setActiveTab(navState.defaultTab);
    }
  }, [location.state, hasOrgNavigation, navState?.defaultTab]);

  // Helper function to log admin actions
  const logAdminAction = async (
    action: string,
    tableName?: string,
    recordId?: string,
    oldValues?: any,
    newValues?: any,
    reason?: string
  ) => {
    try {
      await supabase.rpc('log_admin_action', {
        p_action: action,
        p_table_name: tableName || null,
        p_record_id: recordId || null,
        p_old_values: oldValues || null,
        p_new_values: newValues || null,
        p_reason: reason || null,
      });
    } catch (error) {
      console.error('Failed to log admin action:', error);
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        {import.meta.env.DEV && <RoleDebug />}
        <div className="text-sm text-muted-foreground">Checking admin access…</div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="p-6">
        {import.meta.env.DEV && <RoleDebug />}
        <div className="text-destructive">You do not have admin access.</div>
        <div className="mt-4 text-sm text-muted-foreground">
          If you should have admin access, please contact your system administrator to assign you the 'admin' role.
        </div>
      </div>
    );
  }

  return (
    <AppLayout title="Admin Console | Cybersecurity Assessment" description="Global administrator dashboard for managing users, organizations, and assessments.">
      {/* Admin Warning Banner */}
      <Alert className="mb-6 border-destructive bg-destructive/10">
        <AlertDescription className="font-semibold text-destructive">
          🚨 You are acting as Global Admin. All actions are audited.
        </AlertDescription>
      </Alert>

      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold">Admin Console</h1>
        <div className="flex items-center gap-3">
          {ADMIN_METHODOLOGY_COMPOSITION_UI_ENABLED && (
            <Link
              to="/admin/methodology-composition"
              className="text-sm font-medium text-primary underline-offset-4 hover:underline"
            >
              Methodology &amp; Composition →
            </Link>
          )}

          <Badge variant="secondary">Admin</Badge>
        </div>
      </div>


      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid w-full grid-cols-7">
          <TabsTrigger value="users">Users & Roles</TabsTrigger>
          <TabsTrigger value="organizations">Organizations</TabsTrigger>
          <TabsTrigger value="assessments">Assessments</TabsTrigger>
          <TabsTrigger value="questionnaires">Questionnaires</TabsTrigger>
          <TabsTrigger value="mfa">MFA</TabsTrigger>
          <TabsTrigger value="audit">Audit Log</TabsTrigger>
          <TabsTrigger value="qa_audit">QA Audit</TabsTrigger>
        </TabsList>

        <TabsContent value="users">
          <Card>
            <CardHeader>
              <CardTitle>User Management</CardTitle>
            </CardHeader>
            <CardContent>
              <AdminUsersTable onLogAction={logAdminAction} />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="organizations">
          <Card>
            <CardHeader>
              <CardTitle>Organization Management</CardTitle>
            </CardHeader>
            <CardContent>
              <AdminOrganizationHierarchy onLogAction={logAdminAction} />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="assessments">
          <Card>
            <CardHeader>
              <CardTitle>Assessment Management</CardTitle>
            </CardHeader>
            <CardContent>
              <AdminAssessmentsTable onLogAction={logAdminAction} />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="questionnaires">
          <Card>
            <CardHeader>
              <CardTitle>Questionnaire Management</CardTitle>
            </CardHeader>
            <CardContent>
              <AdminQuestionnairesTable onLogAction={logAdminAction} />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="mfa">
          <AdminMfaPanel />
        </TabsContent>

        <TabsContent value="audit">
          <Card>
            <CardHeader>
              <CardTitle>Audit Log</CardTitle>
            </CardHeader>
            <CardContent>
              <AdminAuditLogTable />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="qa_audit">
          <AdminQaAuditTab />
        </TabsContent>
      </Tabs>

    </AppLayout>
  );
};

export default AdminConsole;